import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { Collapse, IconButton, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { History } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { DocumentHistoryDto } from '../../../types/document';
import useStore from '../../../store/storeContext';
import { convertEpochToDateTimeString } from '../../../utils/dateUtils';

const PREFIX = 'HistoryDialogRow';

const classes = {
    root: `${PREFIX}-root`,
    collapseCell: `${PREFIX}-collapseCell`,
};

const StyledTableRow = styled(TableRow)({
    [`& .${classes.root}`]: {
        '& > *': {
            borderBottom: 'unset',
        },
        cursor: 'pointer',
    },
});

interface Props {
    historyTitle: DocumentHistoryDto;
}

interface State {
    extended: boolean;
}

function HistoryDialogRow(props: Props) {
    const { document, dialog, localization, snackbar, loadingIndicator } = useStore();

    const [state, setState] = useState<State>({
        extended: false,
    });

    async function toggleCollapse(): Promise<void> {
        setState((state) => ({ ...state, extended: !state.extended }));
    }

    async function restore(): Promise<void> {
        const { getConfirmation } = dialog;
        const { restoreDeletedChapter } = document;
        const { translate } = localization;
        try {
            const confirmation = await getConfirmation(
                translate('RESTORE_CHAPTER_TITLE'),
                translate('RESTORE_CHAPTER_TIP') + `: ${props.historyTitle.chapterTitle}`,
                false,
                true,
                translate('RESTORE_DOCUMENT'),
            );
            if (confirmation) {
                loadingIndicator.show();
                await restoreDeletedChapter(props.historyTitle);
                snackbar.showSuccess('SUCCESS');
            }
        } catch (e) {
            snackbar.showError(e?.data?.code);
        } finally {
            loadingIndicator.hide();
        }
    }

    const chapterIsDeletedOrRestored = () => {
        const { chapters } = document.currentDocument;
        if (!props.historyTitle.isDeleted) {
            return true;
        } else if (props.historyTitle.isDeleted && props.historyTitle.chapterId) {
            const historyChapterId = props.historyTitle.chapterId;
            return chapters.map((chp) => chp.id).includes(historyChapterId);
        } else return false;
    };
    const { translate } = localization;
    const { historyTitle } = props;
    return (
        <>
            <StyledTableRow className={classes.root} hover onClick={toggleCollapse}>
                <TableCell>{historyTitle.creator?.userName || ''}</TableCell>
                <TableCell align="right">
                    {historyTitle.dateCreated ? convertEpochToDateTimeString(historyTitle.dateCreated) : ''}
                </TableCell>
            </StyledTableRow>
            <StyledTableRow>
                <TableCell sx={{ width: '95%', borderBottom: 'unset', pl: 4, py: 0 }}>
                    <Collapse in={state.extended} timeout="auto" unmountOnExit>
                        <Typography
                            className="fr-view"
                            dangerouslySetInnerHTML={{ __html: historyTitle.change || '' }}
                            sx={{ mt: 1 }}
                        />
                    </Collapse>
                </TableCell>
                <TableCell sx={{ borderBottom: 'unset', pl: 4, py: 0 }} align="right">
                    <Collapse in={state.extended} timeout="auto" unmountOnExit>
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={restore}
                            disabled={!historyTitle.chapterId || chapterIsDeletedOrRestored()}
                        >
                            <Tooltip title={translate('RESTORE_DOCUMENT')}>
                                <History />
                            </Tooltip>
                        </IconButton>
                    </Collapse>
                </TableCell>
            </StyledTableRow>
        </>
    );
}

export default observer(HistoryDialogRow);
